import React, { useEffect, useMemo, useState } from 'react';
import { apiCall } from '../config';
import { toast, confirmDialog } from '../ui/notify';

// Bedarfsplanung mit Vorlagen je Tagestyp (Philipp, 29.09.2026):
//  • Kalender: jeder Wiesn-Tag mit Tagestyp, Status (wie Vorlage / angepasst / Vorlage geändert / fehlt),
//    Bedarf, Vorlage und eingeplanten Stunden – für das nächste Jahr als Kalkulation (nur Vorlagen)
//  • Vorlagen: Stundenprofil je Station für Mo–Do, Freitag, Samstag, Sonntag, 1. Samstag, 1. Sonntag
//  • Gesicherte Stände: Bedarf aller Tage sichern und bei Bedarf zurückholen
const STATUS = {
  vorlage: { label: 'wie Vorlage', cls: 'bg-success' },
  angepasst: { label: 'angepasst', cls: 'bg-warning text-dark' },
  veraltet: { label: 'Vorlage geändert', cls: 'bg-info text-dark' },
  fehlt: { label: 'kein Bedarf', cls: 'bg-secondary' },
};
const fmtH = (h) => `${String(h % 24).padStart(2, '0')}`;
const dDE = (s) => `${s.slice(8, 10)}.${s.slice(5, 7)}.`;
const n1 = (x) => (Math.round((x || 0) * 10) / 10).toLocaleString('de-DE');
const eur = (x) => (x || 0).toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });

export default function DemandTemplates() {
  const [tab, setTab] = useState('kalender');
  const thisYear = new Date().getFullYear();
  const [year, setYear] = useState(thisYear);
  const [cal, setCal] = useState(null);
  const [season, setSeason] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [snaps, setSnaps] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadCal = async () => {
    try { const c = await apiCall(`/admin/demand/calendar?year=${year}`); setCal(c); setSeason({ ...c.season, holidaysText: (c.season.holidays || []).map(dDE).join(', ') }); }
    catch (e) { toast('Kalender konnte nicht geladen werden: ' + e.message, 'danger'); }
  };
  const loadTpl = async () => { try { setTpl(await apiCall('/admin/demand/templates')); } catch (e) { toast(e.message, 'danger'); } };
  const loadSnaps = async () => { try { setSnaps(await apiCall('/admin/demand/snapshots')); } catch (_) { setSnaps([]); } };

  useEffect(() => { loadCal(); }, [year]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { loadTpl(); loadSnaps(); }, []);

  const parseHolidays = (txt) => String(txt || '').split(/[,;\s]+/).map((t) => t.trim()).filter(Boolean).map((t) => {
    const m = t.match(/^(\d{1,2})\.(\d{1,2})\.?(\d{4})?$/); if (!m) return null;
    return `${m[3] || year}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
  }).filter(Boolean);

  const saveSeason = async (patch = {}) => {
    const s = { ...season, ...patch };
    try {
      await apiCall(`/admin/demand/season/${year}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ start_date: s.start_date, end_date: s.end_date, holidays: parseHolidays(s.holidaysText), type_overrides: s.type_overrides || {}, wage: s.wage }) });
      toast('Saison gespeichert', 'success'); loadCal();
    } catch (e) { toast(e.message, 'danger'); }
  };
  const setDayType = (date, type) => {
    const ov = { ...(season.type_overrides || {}) };
    const auto = cal.days.find((d) => d.date === date);
    if (!type || (auto && !auto.typeOverridden && auto.type === type)) delete ov[date]; else ov[date] = type;
    saveSeason({ type_overrides: ov });
  };

  const apply = async (body, question) => {
    if (question && !(await confirmDialog(question))) return;
    setBusy(true);
    try {
      const r = await apiCall('/admin/demand/apply', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ year, ...body }) });
      toast(r.changed.length ? `${r.changed.length} Tag(e) auf Vorlage gesetzt – vorheriger Stand gesichert (#${r.snapshot})` : 'Nichts zu ändern', 'success');
      loadCal(); loadSnaps();
    } catch (e) { toast(e.message, 'danger'); } finally { setBusy(false); }
  };
  const saveSnapshot = async () => {
    const label = window.prompt('Name für den gesicherten Stand:', `Stand ${new Date().toLocaleDateString('de-DE')}`);
    if (label == null) return;
    try { const r = await apiCall('/admin/demand/snapshots', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ year, label }) }); toast(`Gesichert: ${r.days} Tage`, 'success'); loadSnaps(); }
    catch (e) { toast(e.message, 'danger'); }
  };

  const days = (cal && cal.days) || [];
  const future = days.filter((d) => !d.past);
  const cnt = (st) => future.filter((d) => d.status === st).length;
  const isForecast = days.length && days.every((d) => d.status === 'fehlt');
  const sums = useMemo(() => {
    const s = { need: 0, tpl: 0, planned: 0, persons: 0, per: {} };
    days.forEach((d) => {
      const base = d.need || d.template; if (base) { s.need += base.total; Object.entries(base.perStation).forEach(([k, v]) => { s.per[k] = (s.per[k] || 0) + v; }); }
      if (d.template) s.tpl += d.template.total;
      if (d.planned) { s.planned += d.planned.total; s.persons += d.planned.persons; }
    });
    return s;
  }, [cal]); // eslint-disable-line react-hooks/exhaustive-deps
  const wage = Number(season && season.wage) || 0;

  return (
    <div>
      <ul className="nav nav-tabs mb-3">
        {[['kalender', '📅 Kalender & Kalkulation'], ['vorlagen', '📐 Vorlagen je Tagestyp'], ['staende', '💾 Gesicherte Stände']].map(([k, l]) => (
          <li className="nav-item" key={k}><button className={`nav-link ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{l}</button></li>
        ))}
      </ul>

      {tab === 'kalender' && season && (
        <>
          <div className="card mb-3"><div className="card-body">
            <div className="row g-2 align-items-end">
              <div className="col-auto">
                <label className="form-label small mb-0">Jahr</label>
                <select className="form-select form-select-sm" value={year} onChange={(e) => setYear(Number(e.target.value))}>
                  {[thisYear - 1, thisYear, thisYear + 1, thisYear + 2].map((y) => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div className="col-auto"><label className="form-label small mb-0">Wiesn-Beginn</label>
                <input type="date" className="form-control form-control-sm" value={season.start_date} onChange={(e) => setSeason({ ...season, start_date: e.target.value })} /></div>
              <div className="col-auto"><label className="form-label small mb-0">Ende</label>
                <input type="date" className="form-control form-control-sm" value={season.end_date} onChange={(e) => setSeason({ ...season, end_date: e.target.value })} /></div>
              <div className="col-auto"><label className="form-label small mb-0">Feiertage</label>
                <input className="form-control form-control-sm" style={{ width: 140 }} value={season.holidaysText} placeholder="03.10." onChange={(e) => setSeason({ ...season, holidaysText: e.target.value })} /></div>
              <div className="col-auto"><label className="form-label small mb-0">Stundenlohn (brutto, für Kosten)</label>
                <input type="number" step="0.5" className="form-control form-control-sm" style={{ width: 110 }} value={season.wage ?? ''} placeholder="€/Std" onChange={(e) => setSeason({ ...season, wage: e.target.value })} /></div>
              <div className="col-auto"><button className="btn btn-sm btn-outline-primary" onClick={() => saveSeason()}>Saison speichern</button></div>
              {!season.saved && <div className="col-auto small text-muted">Termine nach Wiesn-Regel vorgeschlagen (Samstag nach dem 15.09. bis 1. Oktober-Sonntag, mind. 03.10.)</div>}
            </div>
          </div></div>

          {!isForecast && (
            <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
              <button className="btn btn-sm btn-outline-secondary" onClick={saveSnapshot}>💾 Aktuellen Stand sichern</button>
              <button className="btn btn-sm btn-primary" disabled={busy || !(cnt('veraltet') + cnt('fehlt'))}
                onClick={() => apply({ mode: 'unberuehrt' })}>Vorlagen übertragen – nur unberührte Tage ({cnt('veraltet') + cnt('fehlt')})</button>
              <button className="btn btn-sm btn-outline-danger" disabled={busy || !(cnt('veraltet') + cnt('fehlt') + cnt('angepasst'))}
                onClick={() => apply({ mode: 'alle' }, `Alle ${cnt('veraltet') + cnt('fehlt') + cnt('angepasst')} künftigen Tage, die von der Vorlage abweichen, auf die Vorlage setzen – auch die ${cnt('angepasst')} von Hand angepassten? Der jetzige Stand wird vorher automatisch gesichert.`)}>
                Alle künftigen Tage auf Vorlage ({cnt('veraltet') + cnt('fehlt') + cnt('angepasst')})</button>
              <small className="text-muted">Vergangene Tage bleiben immer unverändert. Zugeteilte Mitarbeiter werden nicht angefasst.</small>
            </div>
          )}
          {isForecast && <div className="alert alert-info py-2">Kalkulation {year}: Für dieses Jahr gibt es noch keine Tagespläne – alle Zahlen kommen aus den Vorlagen.</div>}

          <div className="table-responsive">
            <table className="table table-sm table-hover align-middle" style={{ fontSize: 13 }}>
              <thead className="table-light"><tr>
                <th>Tag</th><th>Tagestyp</th><th>Status</th>
                {(cal.stations || []).map((s) => <th key={s} className="text-end">{s}</th>)}
                <th className="text-end">Bedarf</th><th className="text-end">Vorlage</th><th className="text-end">Spitze</th>
                {!isForecast && <th className="text-end">Eingeplant</th>}
                {wage > 0 && <th className="text-end">Kosten</th>}
                <th></th>
              </tr></thead>
              <tbody>
                {days.map((d) => {
                  const base = d.need || d.template;
                  const diff = d.need && d.template ? d.need.total - d.template.total : 0;
                  const st = STATUS[d.status] || STATUS.fehlt;
                  return (
                    <tr key={d.date} className={d.today ? 'table-primary' : d.past ? 'text-muted' : ''}>
                      <td className="text-nowrap"><strong>{d.wd} {dDE(d.date)}</strong>{d.holiday && <span className="badge bg-danger ms-1">Feiertag</span>}</td>
                      <td>
                        <select className={`form-select form-select-sm ${d.typeOverridden ? 'border-warning' : ''}`} style={{ width: 190 }} value={d.type} onChange={(e) => setDayType(d.date, e.target.value)} title={d.typeOverridden ? 'Von Hand festgelegt' : 'automatisch'}>
                          {cal.types.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                        </select>
                      </td>
                      <td>{isForecast ? <span className="text-muted">–</span> : <span className={`badge ${st.cls}`}>{st.label}</span>}</td>
                      {(cal.stations || []).map((s) => {
                        const v = base ? base.perStation[s] : 0; const t = d.template ? d.template.perStation[s] : v;
                        return <td key={s} className="text-end">{v}{d.need && t !== v && <small className={v > t ? 'text-danger ms-1' : 'text-success ms-1'}>({v > t ? '+' : ''}{v - t})</small>}</td>;
                      })}
                      <td className="text-end fw-bold">{base ? base.total : '–'}</td>
                      <td className="text-end">{d.template ? d.template.total : '–'}{diff !== 0 && <small className={diff > 0 ? 'text-danger ms-1' : 'text-success ms-1'}>({diff > 0 ? '+' : ''}{diff})</small>}</td>
                      <td className="text-end">{d.needPeak ?? d.templatePeak ?? '–'}</td>
                      {!isForecast && <td className="text-end">{d.planned ? `${n1(d.planned.total)} Std · ${d.planned.persons} P.` : '–'}</td>}
                      {wage > 0 && <td className="text-end">{base ? eur(base.total * wage) : '–'}</td>}
                      <td className="text-end">
                        {!d.past && !isForecast && d.status !== 'vorlage' && (
                          <button className="btn btn-sm btn-outline-secondary py-0" disabled={busy}
                            onClick={() => apply({ dates: [d.date] }, `${d.wd} ${dDE(d.date)} auf die Vorlage „${d.typeLabel}" setzen?${d.status === 'angepasst' ? ' Deine Anpassung an diesem Tag geht dabei verloren (der Stand wird vorher gesichert).' : ''}`)}>↺ Vorlage</button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="table-light fw-bold"><tr>
                <td colSpan={3}>Summe {days.length} Tage</td>
                {(cal.stations || []).map((s) => <td key={s} className="text-end">{sums.per[s] || 0}</td>)}
                <td className="text-end">{sums.need}</td><td className="text-end">{sums.tpl}</td><td></td>
                {!isForecast && <td className="text-end">{n1(sums.planned)} Std · {sums.persons} Schichten</td>}
                {wage > 0 && <td className="text-end">{eur(sums.need * wage)}</td>}
                <td></td>
              </tr></tfoot>
            </table>
          </div>
          <small className="text-muted">Bedarf/Vorlage = Personenstunden (Summe der Personen je Stunde). „Spitze" = höchste Personenzahl gleichzeitig. Tagestyp änderbar – ein von Hand gesetzter Typ ist gelb umrandet.</small>
        </>
      )}

      {tab === 'vorlagen' && tpl && <TemplateEditor data={tpl} onSaved={async (type) => {
        await loadTpl(); await loadCal();
        if (await confirmDialog('Vorlage gespeichert. Jetzt auf alle künftigen Tage übertragen, die du nicht von Hand angepasst hast?')) apply({ mode: 'unberuehrt', label: `Automatisch vor Übertragen (Vorlage ${type} geändert)` });
      }} />}

      {tab === 'staende' && (
        <div>
          <button className="btn btn-sm btn-outline-secondary mb-2" onClick={saveSnapshot}>💾 Aktuellen Stand sichern</button>
          <table className="table table-sm"><thead><tr><th>#</th><th>Gesichert</th><th>Name</th><th>von</th><th></th></tr></thead>
            <tbody>{snaps.map((s) => (
              <tr key={s.id}><td>{s.id}</td><td>{new Date(String(s.created_at).replace(' ', 'T') + 'Z').toLocaleString('de-DE')}</td><td>{s.label}</td><td>{s.created_by || ''}</td>
                <td className="text-end"><button className="btn btn-sm btn-outline-warning py-0" onClick={async () => {
                  if (!(await confirmDialog(`Stand #${s.id} „${s.label}" zurückholen? Gilt für heute und alle künftigen Tage; der jetzige Stand wird vorher gesichert.`))) return;
                  try { const r = await apiCall(`/admin/demand/snapshots/${s.id}/restore`, { method: 'POST' }); toast(`${r.restored} Tage zurückgeholt`, 'success'); loadCal(); loadSnaps(); } catch (e) { toast(e.message, 'danger'); }
                }}>Zurückholen</button></td></tr>
            ))}{!snaps.length && <tr><td colSpan={5} className="text-muted">Noch nichts gesichert.</td></tr>}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TemplateEditor({ data, onSaved }) {
  const { types, stations, hours, templates } = data;
  const [type, setType] = useState('mo_do');
  const [needs, setNeeds] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [fromDate, setFromDate] = useState('');

  useEffect(() => { setNeeds(templates[type] ? JSON.parse(JSON.stringify(templates[type].needs)) : Object.fromEntries(stations.map((s) => [s, Object.fromEntries(hours.map((h) => [h, { needed: 0, needsCashier: false }]))]))); setDirty(false); }, [type, templates]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!needs) return null;
  const set = (s, h, patch) => { setNeeds((p) => ({ ...p, [s]: { ...p[s], [h]: { ...p[s][h], ...patch } } })); setDirty(true); };
  const rowSum = (s) => hours.reduce((a, h) => a + (Number(needs[s][h].needed) || 0), 0);
  const colSum = (h) => stations.reduce((a, s) => a + (Number(needs[s][h].needed) || 0), 0);
  const total = stations.reduce((a, s) => a + rowSum(s), 0);
  const meta = templates[type];

  const loadDay = async () => {
    if (!fromDate) return;
    try { const r = await apiCall(`/admin/demand/day-needs/${fromDate}`); setNeeds(r.needs); setDirty(true); toast(`Bedarf vom ${dDE(fromDate)} geladen – noch nicht gespeichert`, 'info'); }
    catch (e) { toast(e.message, 'danger'); }
  };
  const save = async () => {
    try {
      await apiCall(`/admin/demand/templates/${type}`, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ needs, source_date: fromDate || null }) });
      setDirty(false); onSaved(type);
    } catch (e) { toast(e.message, 'danger'); }
  };

  return (
    <div>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        {types.map((t) => <button key={t.key} className={`btn btn-sm ${type === t.key ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={async () => { if (dirty && !(await confirmDialog('Änderungen an dieser Vorlage verwerfen?'))) return; setType(t.key); }}>{t.label}</button>)}
      </div>
      <div className="d-flex flex-wrap gap-2 align-items-center mb-2">
        <span className="small text-muted">{meta ? `Zuletzt geändert ${new Date(String(meta.updated_at).replace(' ', 'T') + 'Z').toLocaleString('de-DE')}${meta.updated_by ? ' von ' + meta.updated_by : ''}${meta.source_date ? ' · Grundlage ' + dDE(meta.source_date) : ''}` : 'Noch leer'}</span>
        <span className="ms-auto small">Bedarf aus Tag übernehmen:</span>
        <input type="date" className="form-control form-control-sm" style={{ width: 150 }} value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
        <button className="btn btn-sm btn-outline-secondary" onClick={loadDay} disabled={!fromDate}>Laden</button>
        <button className="btn btn-sm btn-success" onClick={save} disabled={!dirty}>Vorlage speichern</button>
      </div>
      <div className="table-responsive">
        <table className="table table-sm table-bordered text-center align-middle" style={{ fontSize: 12 }}>
          <thead className="table-light"><tr><th className="text-start">Station</th>{hours.map((h) => <th key={h} style={{ minWidth: 44 }}>{fmtH(h)}–{fmtH(h + 1)}</th>)}<th>Std</th></tr></thead>
          <tbody>
            {stations.map((s) => (
              <tr key={s}>
                <th className="text-start">{s}</th>
                {hours.map((h) => {
                  const v = needs[s][h];
                  return (
                    <td key={h} className="p-1" style={{ background: v.needed ? `rgba(40,167,69,${Math.min(0.1 + v.needed * 0.08, 0.7)})` : undefined }}>
                      <input type="number" min="0" max="30" className="form-control form-control-sm text-center p-0" style={{ width: 38, margin: '0 auto' }}
                        value={v.needed} onChange={(e) => set(s, h, { needed: Math.max(0, parseInt(e.target.value, 10) || 0) })} />
                      <span role="button" title="Kassenkraft nötig" style={{ opacity: v.needsCashier ? 1 : 0.2, cursor: 'pointer' }} onClick={() => set(s, h, { needsCashier: !v.needsCashier })}>💰</span>
                    </td>
                  );
                })}
                <td className="fw-bold">{rowSum(s)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="table-light fw-bold"><tr><td className="text-start">Gesamt</td>{hours.map((h) => <td key={h}>{colSum(h) || ''}</td>)}<td>{total}</td></tr></tfoot>
        </table>
      </div>
      <small className="text-muted">Zahl = Personen in dieser Stunde, 💰 = Kassenkraft nötig (anklicken). 24–01 = nach Mitternacht. Regel Wiesn-Garten: 23–01 Uhr 2 Personen, Fr/Sa/vor Feiertag 4.</small>
    </div>
  );
}
